// Data hooks for every Settings sub-screen (plan.md G26). Thin wrappers over
// hooks/useApi.ts's useApiQuery/useApiMutation (the shared primitives every web
// module goal builds on), scoped to exactly the endpoints this goal's screens
// call:
//   - services/api/src/modules/config/*.ts       (tenant, sites, cost-centres,
//     locations, products (+pack-spec), users, memberships) — §5.1, §5.2, §6.
//   - services/api/src/modules/xero/*.ts          (connection, mappings, push/
//     pull) — §5.9, §12.
//   - services/api/src/modules/importer/*.ts      (import jobs) — §5.9, §2.13.
import { useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '../../api/client';
import type {
  CostCentreCreateInput,
  CostCentreUpdateInput,
  ImportJobCreateInput,
  ImportJobKind,
  ImportJobStatus,
  LocationCreateInput,
  LocationUpdateInput,
  MembershipCreateInput,
  MembershipUpdateInput,
  PatchTenantSettingsInput,
  ProductCreateInput,
  ProductListQuery,
  ProductUpdateInput,
  Role,
  SiteCreateInput,
  SiteUpdateInput,
  UpsertPackSpecInput,
  XeroConnectResponse,
  XeroConnectionResponse,
  XeroMappingCreate,
  XeroMappingKind,
  XeroMappingResponse,
  XeroMappingUpdate,
  XeroMappingValidationResponse,
  XeroPaymentsPullResponse,
  XeroPushSummaryResponse,
  XeroSyncLogResponse,
  XeroSyncStatus,
} from '@zebra/core';
import { useApiMutation, useApiQuery } from '../../hooks/useApi';
import type {
  CostCentreDto,
  CustomerOptionDto,
  ImportJobDto,
  LocationDto,
  MembershipCreatedDto,
  MembershipRow,
  PackSpecDto,
  PaginatedResponse,
  ProductDto,
  SiteDto,
  TenantDto,
} from './types';

// ---------------------------------------------------------------------------
// Tenant settings (§5.1, §11) — services/api/src/modules/config/tenant.ts
// ---------------------------------------------------------------------------

export function useTenant() {
  return useApiQuery<TenantDto>(['config', 'tenant'], '/config/tenant');
}

export function useUpdateTenantSettings() {
  const qc = useQueryClient();
  return useApiMutation<TenantDto, PatchTenantSettingsInput>('/config/tenant/settings', 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'tenant'] }),
  });
}

// ---------------------------------------------------------------------------
// Sites (§5.1) — services/api/src/modules/config/sites.ts. No delete endpoint
// (no `is_active` column on this table) — create/list/update only.
// ---------------------------------------------------------------------------

export function useSites(params: { q?: string } = {}) {
  return useApiQuery<PaginatedResponse<SiteDto>>(['config', 'sites', params.q ?? 'all'], '/config/sites', {
    query: { limit: 200, q: params.q },
  });
}

export function useCreateSite() {
  const qc = useQueryClient();
  return useApiMutation<SiteDto, SiteCreateInput>('/config/sites', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'sites'] }),
  });
}

export function useUpdateSite(id: string) {
  const qc = useQueryClient();
  return useApiMutation<SiteDto, SiteUpdateInput>(`/config/sites/${id}`, 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'sites'] }),
  });
}

// ---------------------------------------------------------------------------
// Cost centres (§5.1) — services/api/src/modules/config/cost-centres.ts. Same
// shape as sites: no `is_active` column, no delete.
// ---------------------------------------------------------------------------

export function useCostCentres(params: { siteId?: string; q?: string } = {}) {
  return useApiQuery<PaginatedResponse<CostCentreDto>>(
    ['config', 'cost-centres', params.siteId ?? 'all', params.q ?? 'all'],
    '/config/cost-centres',
    { query: { limit: 200, siteId: params.siteId, q: params.q } },
  );
}

export function useCreateCostCentre() {
  const qc = useQueryClient();
  return useApiMutation<CostCentreDto, CostCentreCreateInput>('/config/cost-centres', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'cost-centres'] }),
  });
}

export function useUpdateCostCentre(id: string) {
  const qc = useQueryClient();
  return useApiMutation<CostCentreDto, CostCentreUpdateInput>(`/config/cost-centres/${id}`, 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'cost-centres'] }),
  });
}

// ---------------------------------------------------------------------------
// Locations (§5.2) — services/api/src/modules/config/locations.ts. Silo
// calibration is a nested resource on a SILO location, but that config screen
// is G22's sole ownership (plan.md QC fix #3) — not touched here.
// ---------------------------------------------------------------------------

export function useLocations(params: { siteId?: string; q?: string; enabled?: boolean } = {}) {
  return useApiQuery<PaginatedResponse<LocationDto>>(
    ['config', 'locations', params.siteId ?? 'all', params.q ?? 'all'],
    '/config/locations',
    { query: { limit: 200, siteId: params.siteId, q: params.q }, enabled: params.enabled },
  );
}

export function useCreateLocation() {
  const qc = useQueryClient();
  return useApiMutation<LocationDto, LocationCreateInput>('/config/locations', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'locations'] }),
  });
}

export function useUpdateLocation(id: string) {
  const qc = useQueryClient();
  return useApiMutation<LocationDto, LocationUpdateInput>(`/config/locations/${id}`, 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'locations'] }),
  });
}

// ---------------------------------------------------------------------------
// Products (§5.2) + pack_specs nested resource — services/api/src/modules/
// config/products.ts. Writes are OPERATIONS|ADMIN server-side; this feature
// still gates its own write UI to ADMIN (see permissions.ts).
// ---------------------------------------------------------------------------

export function useProducts(params: { q?: string; type?: ProductListQuery['type']; isActive?: boolean } = {}) {
  return useApiQuery<PaginatedResponse<ProductDto>>(
    ['config', 'products', params.q ?? 'all', params.type ?? 'all', params.isActive ?? 'all'],
    '/config/products',
    { query: { limit: 200, q: params.q, type: params.type, isActive: params.isActive } },
  );
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useApiMutation<ProductDto, ProductCreateInput>('/config/products', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'products'] }),
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ProductDto, ProductUpdateInput>(`/config/products/${id}`, 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'products'] }),
  });
}

/** GET 404s when no pack_spec is configured yet — treated as "unconfigured"
 * rather than a real error by callers (see `isPackSpecMissing`), same pattern
 * as silo-counts's `isCalibrationMissing`. */
export function usePackSpec(packProductId: string | null) {
  return useApiQuery<PackSpecDto>(['config', 'products', packProductId, 'pack-spec'], `/config/products/${packProductId}/pack-spec`, {
    enabled: Boolean(packProductId),
    retry: false,
  });
}

export function isPackSpecMissing(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'NOT_FOUND';
}

export function useUpsertPackSpec(packProductId: string) {
  const qc = useQueryClient();
  return useApiMutation<PackSpecDto, UpsertPackSpecInput>(`/config/products/${packProductId}/pack-spec`, 'PUT', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'products', packProductId, 'pack-spec'] }),
  });
}

// ---------------------------------------------------------------------------
// Users & memberships (§5.1, §6) — GET is users.ts (joined view), writes are
// memberships.ts. ADMIN only, both sides.
// ---------------------------------------------------------------------------

export function useMembers(params: { q?: string } = {}) {
  return useApiQuery<PaginatedResponse<MembershipRow>>(['config', 'users', params.q ?? 'all'], '/config/users', {
    query: { limit: 200, q: params.q },
  });
}

export function useAddMembership() {
  const qc = useQueryClient();
  return useApiMutation<MembershipCreatedDto, MembershipCreateInput>('/config/memberships', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'users'] }),
  });
}

export function useUpdateMembershipRole(membershipId: string) {
  const qc = useQueryClient();
  return useApiMutation<{ id: string; role: Role }, MembershipUpdateInput>(`/config/memberships/${membershipId}`, 'PATCH', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'users'] }),
  });
}

export function useRemoveMembership(membershipId: string) {
  const qc = useQueryClient();
  return useApiMutation<{ id: string; deleted: true }, void>(`/config/memberships/${membershipId}`, 'DELETE', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['config', 'users'] }),
  });
}

// ---------------------------------------------------------------------------
// Xero connection (§5.9, §12) — services/api/src/modules/xero/connection.ts.
// `useXeroConnect` is a GET but modelled as a mutation (`useApiMutation`) since
// it is a deliberate user action ("Connect" button), not a passive display
// query — `useApiMutation`'s `method` param accepts 'GET' directly.
// ---------------------------------------------------------------------------

export function useXeroConnection() {
  return useApiQuery<XeroConnectionResponse>(['xero', 'connection'], '/xero/connection', { retry: false });
}

export function useXeroConnect() {
  return useApiMutation<XeroConnectResponse, void>('/xero/connect', 'GET');
}

export function useXeroDisconnect() {
  const qc = useQueryClient();
  return useApiMutation<XeroConnectionResponse, void>('/xero/disconnect', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['xero', 'connection'] }),
  });
}

// ---------------------------------------------------------------------------
// Xero mappings (§5.9, §12) — services/api/src/modules/xero/mappings.ts.
// ---------------------------------------------------------------------------

export function useXeroMappings(kind?: XeroMappingKind) {
  return useApiQuery<PaginatedResponse<XeroMappingResponse>>(['xero', 'mappings', kind ?? 'all'], '/xero/mappings', {
    query: { limit: 200, kind },
  });
}

export function useXeroMappingValidation() {
  return useApiQuery<XeroMappingValidationResponse>(['xero', 'mappings', 'validate'], '/xero/mappings/validate', {
    retry: false,
  });
}

export function useCreateXeroMapping() {
  const qc = useQueryClient();
  return useApiMutation<XeroMappingResponse, XeroMappingCreate>('/xero/mappings', 'POST', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['xero', 'mappings'] });
    },
  });
}

export function useUpdateXeroMapping(id: string) {
  const qc = useQueryClient();
  return useApiMutation<XeroMappingResponse, XeroMappingUpdate>(`/xero/mappings/${id}`, 'PATCH', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['xero', 'mappings'] });
    },
  });
}

export function useDeleteXeroMapping(id: string) {
  const qc = useQueryClient();
  return useApiMutation<{ id: string; deleted: true }, void>(`/xero/mappings/${id}`, 'DELETE', {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['xero', 'mappings'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Xero push/pull (§12, goal G17) — services/api/src/modules/xero/sync.ts.
// ---------------------------------------------------------------------------

export function usePushPendingToXero() {
  return useApiMutation<XeroPushSummaryResponse, void>('/xero/push', 'POST');
}

export function useSyncXeroPayments() {
  return useApiMutation<XeroPaymentsPullResponse, void>('/xero/sync/payments', 'POST');
}

// ---------------------------------------------------------------------------
// Xero sync log (§5.9, §12, goal G5) — services/api/src/modules/xero/sync-log.ts.
// Read-only, cursor-paginated history of every push/pull attempt already logged
// by the push/pull flows above; no write endpoint exists for this table.
// ---------------------------------------------------------------------------

export function useXeroSyncLog(params: { status?: XeroSyncStatus; entityType?: string; cursor?: string } = {}) {
  return useApiQuery<PaginatedResponse<XeroSyncLogResponse>>(
    ['xero', 'sync-log', params.status ?? 'all', params.entityType ?? 'all', params.cursor ?? 'first'],
    '/xero/sync-log',
    { query: { limit: 50, status: params.status, entityType: params.entityType, cursor: params.cursor } },
  );
}

// ---------------------------------------------------------------------------
// Customers (§5.7) — services/api/src/modules/sales/customers.ts. Read-only
// here: the FBA_SALES job-creation picker's only use for it (goal: "customer
// picker ... Do NOT hardcode any location/customer").
// ---------------------------------------------------------------------------

export function useCustomers(params: { isActive?: boolean; enabled?: boolean } = {}) {
  return useApiQuery<PaginatedResponse<CustomerOptionDto>>(['sales', 'customers', params], '/sales/customers', {
    query: { isActive: params.isActive, limit: 200 },
    enabled: params.enabled,
  });
}

// ---------------------------------------------------------------------------
// Importer (§5.9, §2.13) — services/api/src/modules/importer/jobs.ts. ADMIN
// only on every mutating action; GET (list/detail) open to any authenticated
// tenant member.
// ---------------------------------------------------------------------------

export function useImportJobs(params: { kind?: ImportJobKind; status?: ImportJobStatus } = {}) {
  return useApiQuery<PaginatedResponse<ImportJobDto>>(
    ['importer', 'jobs', params.kind ?? 'all', params.status ?? 'all'],
    '/importer/jobs',
    { query: { limit: 100, kind: params.kind, status: params.status } },
  );
}

export function useImportJob(id: string | undefined) {
  return useApiQuery<ImportJobDto>(['importer', 'jobs', 'byId', id], `/importer/jobs/${id}`, {
    enabled: Boolean(id),
  });
}

export function useCreateImportJob() {
  const qc = useQueryClient();
  return useApiMutation<ImportJobDto, ImportJobCreateInput>('/importer/jobs', 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['importer', 'jobs'] }),
  });
}

export function useValidateImportJob(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ImportJobDto, void>(`/importer/jobs/${id}/validate`, 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['importer', 'jobs'] }),
  });
}

export function useCommitImportJob(id: string) {
  const qc = useQueryClient();
  return useApiMutation<ImportJobDto, void>(`/importer/jobs/${id}/commit`, 'POST', {
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['importer', 'jobs'] }),
  });
}
